import { type ChangeEvent, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  TextField,
  Typography,
  Chip,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContentText,
  DialogContent
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
// import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArticleIcon from "@mui/icons-material/Article";
import Grid from "@mui/material/Grid";
import CustomTypography from "../../../ui-component/CustomUiComponent/Typography";
import SelectOption from "../../../ui-component/CustomUiComponent/SelectOption";
import debounce from "lodash.debounce";
import CustomButton from "../../../ui-component/CustomUiComponent/Button";
import useIntranetPortalCoreApi from "../../../hooks/useIntranetPortalAdminApi";
import {
  DepartmentService,
  DesignationService,
  EmployeeService,
  UserProfileService,
  type IntranetPortal_Employees_Dtos_EmployeeDto,
  type Volo_Abp_Application_Dtos_PagedResultDto_1OfOfIntranetPortal_Departments_Dtos_DepartmentDto_IntranetPortal_Application_Contracts,
  type Volo_Abp_Application_Dtos_PagedResultDto_1OfOfIntranetPortal_Designations_Dtos_ActiveDesignationDto_IntranetPortal_Application_Contracts,
  type Volo_Abp_Application_Dtos_PagedResultDto_1OfOfIntranetPortal_Employees_Dtos_EmployeeDto_IntranetPortal_Application_Contracts
} from "../../../services/intranet-portal-services";
import CustomTable from "../../../ui-component/CustomUiComponent/TableContainer";
import CustomPagination from "../../../ui-component/CustomUiComponent/Pagination";
import { useNavigate } from "react-router-dom";
import { type CellProps } from "react-table";
import usePageTracking from "../../../utils/withPageTracking";
import CustomAvatar from "../../../ui-component/CustomUiComponent/Avatar";
import { CustomCircularProgress } from "../../../ui-component/CustomUiComponent/CircularProgress";
import { type ReactTableColumn } from "@/types";
import { getProfilePictureUrl } from "../../../utils/imageUtils";
import { type SelectChangeEvent } from "@mui/material";

import ActionMenu, {
  type ActionMenuItem
} from "../../../ui-component/CustomUiComponent/ActionMenu";
import { toast } from "react-toastify";
import useHasPermission from "../../../hooks/useHasPermission";

interface EmployeeTableColumns extends IntranetPortal_Employees_Dtos_EmployeeDto {}

// Employee status options for filter
const employeeStatusOptions = [
  // { id: "all", name: "All" },
  { id: "active", name: "Active" },
  { id: "inactive", name: "Inactive" }
];

const MIN_PHONE_LENGTH = 10;
const DEBOUNCE_DELAY = 500;
const DEFAULT_PAGE_SIZE = 10;
const TOGGLE_STATUS_PERMISSION = "IntranetPortal.UserProfileAdmin.ToggleActivationStatus";

const AllEmployee = (): React.ReactElement => {
  const navigate = useNavigate();

  const canToggleStatus = useHasPermission(TOGGLE_STATUS_PERMISSION);

  const { handleRequest, isLoading } = useIntranetPortalCoreApi();
  const [data, setData] =
    useState<Volo_Abp_Application_Dtos_PagedResultDto_1OfOfIntranetPortal_Employees_Dtos_EmployeeDto_IntranetPortal_Application_Contracts>();
  const [desgination, setDesignation] =
    useState<Volo_Abp_Application_Dtos_PagedResultDto_1OfOfIntranetPortal_Designations_Dtos_ActiveDesignationDto_IntranetPortal_Application_Contracts>();
  const [department, setDepartment] =
    useState<Volo_Abp_Application_Dtos_PagedResultDto_1OfOfIntranetPortal_Departments_Dtos_DepartmentDto_IntranetPortal_Application_Contracts>();

  const [searchKeyword, setSearchKeyword] = useState("");
  const [sortType, setSortType] = useState("asc");
  const [departmentId, setDepartmentId] = useState<any>(null);
  const [designationId, setDesignationId] = useState<any>(null);
  const [employeeStatus, setEmployeeStatus] = useState<string | null>("active");
  const [sorting, setSorting] = useState("");
  // const [skipCount, setSkipCount] = useState(0);
  const [maxResultCount, setMaxResultCount] = useState(DEFAULT_PAGE_SIZE);

  const [page, setPage] = useState(0);
  const skipCount = page * maxResultCount;

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: "" as "activate" | "deactivate" | "",
    employee: null as EmployeeTableColumns | null
  });

  usePageTracking();

  // GET EMPLOYEE DATA
  const getEmployeeData = useCallback(async (): Promise<void> => {
    const isActive =
      employeeStatus === "active" ? true : employeeStatus === "inactive" ? false : undefined;

    const response = await handleRequest(() =>
      EmployeeService.getCoreApiApiAppEmployeePagedAndSortedEmployeeList(
        searchKeyword,
        sortType,
        departmentId,
        designationId,
        isActive,
        sorting,
        skipCount,
        maxResultCount
      )
    );

    if (response !== null && response !== undefined) {
      setData(response);
    }
  }, [
    searchKeyword,
    sortType,
    departmentId,
    designationId,
    employeeStatus,
    sorting,
    skipCount,
    maxResultCount,
    handleRequest
  ]);

  // GET DESIGNATION
  const getDesignations = useCallback(async (): Promise<void> => {
    const response = await handleRequest(() =>
      DesignationService.getCoreApiApiAppDesignationActiveDesignations()
    );
    if (response !== null && response !== undefined) {
      setDesignation(response);
    }
  }, [handleRequest]);

  // GET DEPARTMENT
  const getDepartments = useCallback(async (): Promise<void> => {
    const response = await handleRequest(() =>
      DepartmentService.getCoreApiApiAppDepartmentActiveDepartments()
    );
    if (response !== null && response !== undefined) {
      setDepartment(response);
    }
  }, [handleRequest]);

  // get designations and departments on mount
  useEffect(() => {
    const fetchMetadata = async (): Promise<void> => {
      await getDepartments();
      await getDesignations();
    };

    void fetchMetadata();
  }, [getDepartments, getDesignations]);

  // calling api for employeeData
  useEffect(() => {
    void getEmployeeData();
  }, [getEmployeeData]);

  const putInTableFirstPage = useCallback((): void => {
    setPage(0);
    // setSkipCount(0);
  }, []);

  const handleOpenConfirmDialog = useCallback(
    (action: "activate" | "deactivate", employee: EmployeeTableColumns): void => {
      setConfirmDialog({
        open: true,
        action,
        employee
      });
    },
    []
  );

  const handleCloseConfirmDialog = (): void => {
    setConfirmDialog({
      open: false,
      action: "",
      employee: null
    });
  };

  const handleConfirmToggleStatus = useCallback(async (): Promise<void> => {
    if (!canToggleStatus || !confirmDialog.employee) {
      return;
    }

    const { employee, action } = confirmDialog;
    const employeeId = employee.abpUserId as string;
    const toggleStatus = action === "activate";

    const response = await handleRequest(() =>
      UserProfileService.postCoreApiApiAppUserProfileToggleUserActivationStatus(
        employeeId,
        toggleStatus
      )
    );

    handleCloseConfirmDialog();

    if (response !== null && response !== undefined) {
      toast.success(`Employee ${action === "activate" ? "activated" : "deactivated"} successfully`);
    }

    try {
      await getEmployeeData();
    } catch (refreshError) {
      console.warn("Failed to refresh employee list:", refreshError);
    }
  }, [canToggleStatus, confirmDialog, handleRequest, getEmployeeData, handleCloseConfirmDialog]);

  const columns: Array<ReactTableColumn<EmployeeTableColumns>> = useMemo(
    () => [
      {
        Header: "S.No",
        Cell: ({ row }: CellProps<EmployeeTableColumns>) => {
          return <Box component="span">{skipCount + row.index + 1}</Box>;
        }
      },
      {
        Header: "",
        accessor: "profilePictureUrl",
        Cell: ({ row }: CellProps<EmployeeTableColumns>) => {
          const { profilePictureUrl, email } = row.original;
          return (
            <CustomAvatar
              variant="circular"
              label={email?.charAt(0)}
              src={getProfilePictureUrl(profilePictureUrl)}
            />
          );
        }
      },
      {
        Header: "Name",
        accessor: "fullName",
        Cell: ({ row }: CellProps<EmployeeTableColumns>) => {
          return row?.original?.name != null ? (
            <Typography
              sx={{ cursor: "pointer" }}
              onClick={() => {
                void navigate(`/pages/employee-details?id=${row.original.abpUserId as string}`);
              }}
            >
              {row.original.fullName}
            </Typography>
          ) : (
            <Typography
              sx={{ cursor: "pointer" }}
              onClick={() => {
                void navigate(`/pages/employee-details?id=${row.original.abpUserId as string}`);
              }}
              variant="caption"
            >
              -------------------
            </Typography>
          );
        }
      },
      {
        Header: "Email",
        accessor: "email"
      },
      {
        Header: "Phone Number",
        accessor: "phoneNumber",
        Cell: ({ row }: CellProps<EmployeeTableColumns>) => {
          const { phoneNumber } = row.original;
          return (phoneNumber?.length ?? 0) < MIN_PHONE_LENGTH ? (
            <Typography variant="caption">-------------------</Typography>
          ) : (
            <Typography>{row.original.phoneNumber}</Typography>
          );
        }
      },
      {
        Header: "Status",
        Cell: ({ row }: CellProps<EmployeeTableColumns>) => {
          const isActive = row.original.isActive;
          const status = isActive === undefined ? "Unknown" : isActive ? "Active" : "Inactive";

          return (
            <Chip
              label={status}
              size="small"
              sx={{
                fontWeight: "500",
                color: "#fff"
              }}
              color={isActive === true ? "success" : isActive === false ? "error" : "default"}
            />
          );
        }
      },

      {
        Header: "Department",
        accessor: "departmentName",
        hidden: true
      },
      {
        Header: "Designation",
        accessor: "designationName",
        hidden: true
      },
      {
        Header: "Hired Date",
        accessor: "hiredDate",
        hidden: true
      },
      ...(canToggleStatus
        ? [
            {
              Header: "Actions",
              Cell: ({ row }: CellProps<EmployeeTableColumns>) => {
                const employee = row.original;
                const viewAcknowledgementsItem: ActionMenuItem = {
                  label: "Document Acknowledgements",
                  icon: <ArticleIcon fontSize="small" />,
                  color: "info",
                  onClick: () => {
                    void navigate(
                      `/pages/employee-document-acknowledgements?id=${employee.abpUserId as string}&name=${encodeURIComponent(employee.fullName ?? "")}`
                    );
                  }
                };

                const menuItems: ActionMenuItem[] =
                  employee.isActive === true
                    ? [
                        // {
                        //   label: "View Employee",
                        //   icon: <VisibilityOutlinedIcon fontSize="small" />,
                        //   color: "info",
                        //   onClick: () => {
                        //     void navigate(`/pages/employee-details?id=${employee?.abpUserId}`);
                        //   }
                        // },
                        viewAcknowledgementsItem,
                        {
                          label: "Deactivate Employee",
                          icon: <BlockIcon fontSize="small" />,
                          color: "error",
                          onClick: () => {
                            handleOpenConfirmDialog("deactivate", employee);
                          }
                        }
                      ]
                    : [
                        // {
                        //   label: "View Employee",
                        //   icon: <VisibilityOutlinedIcon fontSize="small" />,
                        //   color: "info",
                        //   onClick: () => {
                        //     void navigate(`/pages/employee-details?id=${employee?.abpUserId}`);
                        //   }
                        // },
                        viewAcknowledgementsItem,
                        {
                          label: "Activate Employee",
                          icon: <CheckCircleOutlineIcon fontSize="small" />,
                          color: "success",
                          onClick: () => {
                            handleOpenConfirmDialog("activate", employee);
                          }
                        }
                      ];

                return <ActionMenu items={menuItems} />;
              }
            }
          ]
        : [])
    ],
    [skipCount, canToggleStatus, navigate, handleOpenConfirmDialog, getProfilePictureUrl]
  );

  // HANDLE ORDER
  const handleOrder = useCallback((id: string): void => {
    sortType === "asc" ? setSortType("desc") : setSortType("asc");
    setSorting(id);
  }, []);

  // SEARCH
  const onSearchTermInput = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchKeyword(event.target.value);
    putInTableFirstPage();
  };

  const debouncedChangeHandler = useMemo(() => debounce(onSearchTermInput, DEBOUNCE_DELAY), []);

  // PAGINATION
  const handleLimitChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    setMaxResultCount(Number(event.target.value));
    putInTableFirstPage();
  }, []);

  const handlePageChange = useCallback(
    (event: React.ChangeEvent<unknown> | null, value: number): void => {
      // setSkipCount(maxResultCount * value);
      setPage(value);
    },
    []
  );

  // CLEAR
  const handleSearchClear = (): void => {
    // clear input field
    const input = document.getElementsByTagName("input");
    for (let i = 0; i < input.length; i++) {
      input[i].value = "";
    }
    setSearchKeyword("");
    setDepartmentId(null);
    setDesignationId(null);
    setEmployeeStatus(null);
  };

  return (
    <Fragment>
      <Box>
        <Grid container spacing={2} mb={2} alignItems={"center"}>
          <Grid item xs={12}>
            <CustomTypography
              variant="h5"
              sx={{ fontWeight: "500", color: "#D42C2C" }}
              label={"All Employee"}
            />
          </Grid>
        </Grid>
      </Box>

      <Box>
        <Grid container spacing={2} sx={{ py: "1rem" }}>
          <Grid item xs={12} sm={6} md={4} lg={2.5}>
            <TextField
              label="Search Employee Name"
              fullWidth
              size="small"
              id="filled-basic"
              onChange={debouncedChangeHandler}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.5}>
            {desgination?.items !== null && (
              <SelectOption
                label="Select Designation"
                name="designation"
                value={designationId != null ? designationId : ""}
                onChange={(event) => {
                  setDesignationId(event.target.value);
                  putInTableFirstPage();
                }}
                options={desgination?.items}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.5}>
            {department?.items !== null && (
              <SelectOption
                label="Select Department"
                name="department"
                value={departmentId != null ? departmentId : ""}
                onChange={(event) => {
                  setDepartmentId(event.target.value);
                  putInTableFirstPage();
                }}
                options={department?.items}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.5}>
            <SelectOption
              label="Employee Status"
              name="employeeStatus"
              value={employeeStatus !== null ? employeeStatus : ""}
              onChange={(event: SelectChangeEvent<string>) => {
                setEmployeeStatus(event.target.value);
                putInTableFirstPage();
              }}
              options={employeeStatusOptions}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={2}
            sx={{
              display: "flex",
              alignItems: "center"
            }}
          >
            <CustomButton
              sx={{ width: "100%" }}
              label={"Clear"}
              variant={"contained"}
              onClick={handleSearchClear}
              color="error"
            />
          </Grid>
        </Grid>
      </Box>

      <Card sx={{ mb: "1rem" }} elevation={1}>
        <CardContent sx={{ p: 2 }}>
          <Box component={Paper}>
            {isLoading ? (
              <Grid container direction="row" justifyContent="center" alignItems="center">
                <CustomCircularProgress />
              </Grid>
            ) : (
              <>
                {data?.items != null && data.items.length > 0 ? (
                  <CustomTable
                    columns={columns}
                    key={canToggleStatus ? "admin-table" : "employee-table"}
                    data={data?.items}
                    handleSort={handleOrder}
                  />
                ) : (
                  <Box component="span" style={{ alignContent: "center" }}>
                    No Record Available
                  </Box>
                )}
              </>
            )}
            {data?.items != null && data.items.length > 0 && (
              <CustomPagination
                component={"div"}
                count={data?.totalCount ?? 0}
                onPageChange={(event, value) => {
                  handlePageChange(event, value);
                }}
                onRowsPerPageChange={handleLimitChange}
                page={page}
                rowsPerPage={maxResultCount}
                rowsPerPageOptions={[5, 10, 15, 20, 25]}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
      >
        <DialogTitle id="confirmation-dialog-title">
          Confirm {confirmDialog.action === "activate" ? "Activation" : "Deactivation"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirmation-dialog-description">
            Are you sure you want to {confirmDialog.action} employee{" "}
            <strong>{confirmDialog.employee?.fullName}</strong>?
            {confirmDialog.action === "deactivate" && (
              <Box component="span" sx={{ display: "block", mt: 1, color: "error.main" }}>
                {"This will restrict the employee's access to the system."}
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <CustomButton
            label="Cancel"
            onClick={handleCloseConfirmDialog}
            variant="outlined"
            color="inherit"
          />
          <CustomButton
            label={confirmDialog.action === "activate" ? "Activate" : "Deactivate"}
            onClick={handleConfirmToggleStatus}
            variant="contained"
            color={confirmDialog.action === "activate" ? "success" : "error"}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default AllEmployee;
